import React, { useRef, useState, useEffect, useCallback } from 'react';
import MaterialTable from 'material-table';
import { useTranslation } from 'react-i18next';
import { Link, Typography, Grid } from '@material-ui/core';
import { getPersonTournamentsReq, getMyTournamentsReq } from '../../api/tournamentCalendar/listApi';
import { TournamentCalendarModel } from '../../models/tournamentCalendarModel';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../stores';
import { AuthState } from '../../stores/auth/types';
import { ProviderCtx } from '../../config/providerCtx';
import { TournamentRoles } from '../../models/eums/tournamentRoles';
import { localization } from 'config/localization';

interface Props {
  forPerson?: string;
  showToolbar?: boolean;
}

export function PersonTournamentsTable(props: Props) {
  const { t } = useTranslation();
  const dataTableRef = useRef<MaterialTable<TournamentCalendarModel>>(null);
  const [tournaments, setTournaments] = useState<TournamentCalendarModel[]>([]);
  const history = useHistory();
  const { personId: currentPersonId } = useSelector<ApplicationState, AuthState>(
    state => state.auth,
  );
  const { forPerson, showToolbar } = props;

  async function getTournaments(personId?: string) {
    let tournamentsResp;
    if (personId) tournamentsResp = await getPersonTournamentsReq(personId);
    else tournamentsResp = await getMyTournamentsReq();

    if (tournamentsResp && tournamentsResp.data) setTournaments(tournamentsResp.data);
  }

  const getTournamentsMemo = useCallback(() => getTournaments(forPerson), [forPerson]);

  useEffect(() => {
    getTournamentsMemo();
  }, [getTournamentsMemo]);

  return (
    <MaterialTable<TournamentCalendarModel>
      options={{
        pageSize: 10,
        showTitle: true,
        toolbar: typeof showToolbar === 'boolean' ? showToolbar : true,
        search: true,
        headerStyle: {
          fontWeight: 'bold',
          backgroundColor: '#F5F5F5',
        },
      }}
      title={t('my_tournaments')}
      tableRef={dataTableRef}
      localization={localization()}
      columns={[
        {
          title: t('tournament'),
          field: 'name',
          sorting: true,
          render: val => (
            <Link onClick={() => history.push(`/tournament-calendar/tournament/${val._id}`)}>
              {`${val.name || ''}`}
            </Link>
          ),
        },
        {
          title: t('position'),
          sorting: false,
          render: val => {
            const positions: string[] = [];
            if (
              val.tournamentDirector?.personUUID.personId === (forPerson || currentPersonId) &&
              val.tournamentDirector?.personUUID.providerId === ProviderCtx
            )
              positions.push(TournamentRoles.SUPERVISOR);

            (val.officials || []).forEach(official => {
              const { personId, providerId } = official.person.personUUID;
              if (personId === (forPerson || currentPersonId) && providerId === ProviderCtx)
                positions.push(official.role);
            });

            return (
              <Grid container item direction='column' component='div'>
                {positions.map((element, index) => (
                  <Typography variant='body2' key={index}>
                    {element}
                  </Typography>
                ))}
              </Grid>
            );
          },
        },
        {
          title: t('tournament_start'),
          field: 'start',
          sorting: true,
          render: val => val.start && moment(val.start).format('DD.MM.YYYY.'),
        },
        {
          title: t('tournament_end'),
          field: 'end',
          sorting: true,
          render: val => val.end && moment(val.end).format('DD.MM.YYYY.'),
        },
        {
          title: t('rank'),
          field: 'rank',
          sorting: true,
        },
        {
          title: t('category'),
          field: 'category',
          sorting: true,
        },
      ]}
      data={tournaments}
    />
  );
}
